import { getChildAvatarSrc } from '../../utils/childAvatars';
import type { ChildAvatarKey } from '../../types/assessment.types';

interface ChildProgressBarProps {
  /** Child's avatar key (e.g., 'cat', 'bear') */
  avatarKey: ChildAvatarKey;
  /** Child's nickname */
  childName: string;
  /** Child's minimum age */
  fromAge: number;
  /** Child's maximum age */
  toAge: number;
  /** Number of questions answered */
  answeredCount: number;
  /** Total number of questions */
  totalCount: number;
}

/**
 * ChildProgressBar displays child information and question progress
 * at the bottom of assessment pages.
 *
 * Design specs (steps.md:59-106):
 * - White background with rounded top corners (36px)
 * - Blue shadow: 0px 0px 16px rgba(0, 124, 173, 0.2)
 * - Child avatar: 36×36px circular
 * - Layout: avatar + name/age on left, count + label on right
 */
export default function ChildProgressBar({
  avatarKey,
  childName,
  fromAge,
  toAge,
  answeredCount,
  totalCount,
}: ChildProgressBarProps) {
  const avatarSrc = getChildAvatarSrc(avatarKey);

  return (
    <div
      className="bg-white rounded-t-[36px] p-4"
      style={{
        boxShadow: '0px 0px 16px rgba(0, 124, 173, 0.2)',
      }}
    >
      <div className="flex items-center justify-between">
        {/* Left section: Avatar + Name/Age */}
        <div className="flex items-center gap-4">
          {/* Child Avatar */}
          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={`${childName}'s avatar`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#C7EDEF] flex items-center justify-center">
                <span className="text-[#1B2831] text-sm font-bold">
                  {childName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Child Name & Age */}
          <div className="flex flex-col">
            <p className="text-base font-black text-[#1B2831] leading-tight">
              {childName}
            </p>
            <p className="text-xs font-medium text-[#4D5762] leading-tight">
              {fromAge}-{toAge} years
            </p>
          </div>
        </div>

        {/* Right section: Progress Count + Label */}
        <div className="flex flex-row items-baseline items-end gap-2">
          <p
            className="text-lg font-bold text-[#01A7C8]"
            style={{ letterSpacing: '-0.72px' }}
          >
            {answeredCount}/{totalCount}
          </p>
          <p className="text-xs font-medium text-[#4D5762] leading-tight">
            Questions answered
          </p>
        </div>
      </div>
    </div>
  );
}
